import { useCallback, useEffect, useState } from 'react';

import { ApiError, listTimeline, type TimelineEvent } from '../api.js';
import { formatDate } from '../labels.js';

/**
 * The project's story, in order, from the records themselves (TL-01).
 *
 * Not a view over the audit trail — the trail is the security record. Each
 * event arrives as its own sentence, said by the module that owns it; this
 * screen never re-words a record. The server decides which kinds an external
 * caller sees (a whitelist), so there is no filtering to get wrong here.
 */

interface Props {
  readonly projectId: string;
}

const FIRST_PAGE = 30;
const FULL_PAGE = 300;

export function Timeline({ projectId }: Props): JSX.Element {
  const [events, setEvents] = useState<readonly TimelineEvent[] | null>(null);
  /** A denied read is not an empty list and not a failure. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(
    async (limit: number) => {
      try {
        setEvents(await listTimeline(projectId, limit));
        setDenied(false);
        setError(null);
      } catch (caught) {
        const refused =
          caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
        setDenied(refused);
        setError(refused ? null : 'Could not load the timeline.');
        setEvents([]);
      }
    },
    [projectId],
  );

  useEffect(() => {
    void load(FIRST_PAGE);
  }, [load]);

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Timeline</h3>
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">Your role does not include this project&rsquo;s timeline.</p>
      </>
    );
  }

  if (events === null) {
    return (
      <>
        {head}
        <p aria-live="polite">Loading the timeline…</p>
      </>
    );
  }

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {events.length === 0 ? (
        <div className="empty">
          <p>
            <strong>Nothing on the timeline yet.</strong>
          </p>
          <p>
            Events appear here as the project produces records — a submitted diary, an issued
            drawing, a completed stage.
          </p>
        </div>
      ) : (
        <>
          <ol className="timeline-list">
            {events.map((event, index) => (
              <li key={`${event.kind}-${event.occurred_on}-${index}`}>
                <span className="mono">{formatDate(event.occurred_on)}</span> —{' '}
                <span>{event.title}</span>
                {event.detail && <span className="hint"> · {event.detail}</span>}
                {event.actor_name && <span className="hint"> · {event.actor_name}</span>}
              </li>
            ))}
          </ol>
          {!expanded && events.length >= FIRST_PAGE && (
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setExpanded(true);
                void load(FULL_PAGE);
              }}
            >
              Show the full history
            </button>
          )}
        </>
      )}
    </>
  );
}
